import React from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { formatCurrency } from 'utils';
import { grey } from '@mui/material/colors';
import { DateFormats } from 'constants/common';
import { Listing } from 'components/shared/TableListing';
import { OptionsList } from 'components/shared/OptionsList';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Delete, Edit, CalendarToday } from '@mui/icons-material';
import { capitalizeFirstLetter, formatEnumValue } from 'formatters';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';

import { PageViewType } from '../table/types';
import { DealSheetConfigurationViewModel } from '../../types';

interface Props {
  isConfigLoading: boolean;
  config: Nullable<DealSheetConfigurationViewModel>;
  onChangeView: (typ: PageViewType, id: string | null) => void;
}

export const ConfigurationDetails = ({ config, onChangeView }: Props) => {
  if (!config) return null;

  const panelInfoRender = [
    {
      prefix: 'Author: ',
      value: config.createdBy.fullName,
      icon: <Delete color="disabled" />,
    },
    {
      prefix: 'Updated: ',
      icon: <Edit color="disabled" />,
      value: !config.updatedDateTime
        ? 'Not updated'
        : moment(config.updatedDateTime).format(DateFormats.DateTimeMedium24Hour),
    },
    {
      prefix: 'Created: ',
      icon: <CalendarToday color="disabled" />,
      value: moment(config.createdDateTime).format(DateFormats.DateTimeMedium24Hour),
    },
  ];

  console.log(config.charges.data.filter(charge => charge.type === 'CHARGE'));

  return (
    <Stack className="salesCenterCard" gap="1rem" height="100%" padding="0 0.5rem">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="1rem">
        <Stack gap="0.2rem">
          <Typography variant="h6">{config.name}</Typography>

          {config.description && (
            <Typography variant="body1" color="textDisabled">
              {config.description}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" gap="1rem" alignItems="center">
          {panelInfoRender.map(({ icon, prefix, value }) => (
            <Box
              key={prefix}
              gap="0.25rem"
              display="flex"
              alignItems="center"
              sx={{
                borderRadius: '0.25rem',
                padding: '0.4rem 0.3rem',
                backgroundColor: grey[50],
                boxShadow: ({ shadows }) => shadows[1],
              }}
            >
              {icon}
              <Typography color="textDisabled">{prefix}</Typography>
              <Typography>{value}</Typography>
            </Box>
          ))}

          <Button startIcon={<Edit />} variant="contained" onClick={() => onChangeView('update', config.id)}>
            Edit
          </Button>

          <Button variant="contained" onClick={() => onChangeView('tabs', null)}>
            Back
          </Button>
        </Stack>
      </Stack>

      <TabsContainer
        tabs={[
          {
            value: 'conditions',
            label: 'Conditions',
            component: (
              <>
                {Object.entries(config.dealRules)
                  .filter(([, rules]) => !!rules && typeof rules !== 'string')
                  .map(([ruleGroup, rules]: any) => (
                    <Stack key={ruleGroup} gap="0.5rem">
                      <Typography fontWeight="bold" variant="subtitle1" component="span">
                        Group {capitalizeFirstLetter(ruleGroup)}:
                      </Typography>

                      <Stack direction="row" gap="1.5rem">
                        <OptionsList
                          options={Object.entries(rules)
                            .filter(([, value]) => !!value)
                            .map(([rule, value]: any) => ({
                              label: capitalizeFirstLetter(rule),
                              value: Array.isArray(value)
                                ? value.map(formatEnumValue).join(', ')
                                : formatEnumValue(value),
                            }))}
                        />
                      </Stack>
                    </Stack>
                  ))}
              </>
            ),
          },
          {
            value: 'Charges',
            label: 'Charges & Fees',
            component: (
              <Listing<DealSheetConfigurationViewModel['charges']['data'][number]>
                tableData={config.charges.data.filter(charge => charge.type === 'CHARGE')}
                columns={[
                  {
                    renderKey: 'name',
                    header: 'Charge & Fee Name',
                    renderCell: ({ row }) => <Typography>{row.name}</Typography>,
                  },
                  {
                    header: 'Description',
                    renderKey: 'description',
                    renderCell: ({ row }) => <Typography>{row.description ?? '-'}</Typography>,
                  },
                  {
                    header: 'Taxable',
                    renderKey: 'isTaxable',
                    renderCell: ({ row }) => <Typography>{row.isTaxable ? 'Yes' : 'No'}</Typography>,
                  },
                  {
                    header: 'AmountType',
                    renderKey: 'amountType',
                    renderCell: ({ row }) => <Typography>{capitalizeFirstLetter(row.amountType)}</Typography>,
                  },
                  {
                    header: 'Amount',
                    renderKey: 'amount',
                    renderCell: ({ row }) => (
                      <Typography>
                        {row.amountType === 'flat' ? formatCurrency(row.amount) : `${row.amount}%`}
                      </Typography>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            value: 'tax',
            label: 'Tax',
            component: (
              <Listing<DealSheetConfigurationViewModel['charges']['data'][number]>
                tableData={config.charges.data.filter(charge => charge.type === 'TAX')}
                columns={[
                  {
                    renderKey: 'name',
                    header: 'Tax Name',
                    renderCell: ({ row }) => <Typography>{row.name}</Typography>,
                  },
                  {
                    header: 'Description',
                    renderKey: 'description',
                    renderCell: ({ row }) => <Typography>{row.description ?? '-'}</Typography>,
                  },
                  {
                    header: 'Tax Rate (%)',
                    renderKey: 'amount',
                    renderCell: ({ row }) => <Typography>{row.amount}%</Typography>,
                  },
                ]}
              />
            ),
          },
        ]}
      />
    </Stack>
  );
};
